/**
 * Global Error Boundary Component
 *
 * Catches React errors throughout the application tree and provides:
 * - Authentication error detection (401/403)
 * - Automatic redirect to login for auth failures
 * - Fallback UI for non-authentication errors
 * - Error logging for debugging
 *
 * Based on: docs/integration/auth/FRONTEND_AUTH_GUIDE.md
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { isAuthError } from '../lib/api'
import { authService } from '../services/auth.service'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Global Error Boundary
 * Wraps the application to catch and handle errors gracefully
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  /**
   * Update state when an error is caught
   * This method is called during the render phase
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('[ErrorBoundary] Error caught:', error)
    return {
      hasError: true,
      error,
    }
  }

  /**
   * Handle the error after it's been caught
   * This method is called during the commit phase
   */
  async componentDidCatch(error: Error, errorInfo: ErrorInfo): Promise<void> {
    console.error('[ErrorBoundary] ❌ Component error details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })

    // Store error info in state
    this.setState({
      errorInfo,
    })

    // Check if this is an authentication error
    if (isAuthError(error)) {
      console.log('[ErrorBoundary] 🔒 Authentication error detected - clearing session')

      try {
        await authService.logout()

        // Clear any cached auth data
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('token')
          window.sessionStorage.clear()
        }

        // Redirect to login page
        console.log('[ErrorBoundary] Redirecting to login...')
        window.location.href = '/login'
      } catch (signOutError) {
        console.error('[ErrorBoundary] ❌ Error during sign out:', signOutError)
        // Force redirect even if sign out fails
        window.location.href = '/login'
      }
    }

    // Log error to monitoring service (if implemented)
    // this.logErrorToService(error, errorInfo);
  }

  /**
   * Reset error boundary state
   * Allows user to attempt recovery
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  /**
   * Render fallback UI when error is caught
   * Or render children if no error
   */
  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Check if auth error (will redirect, so show minimal UI)
      if (isAuthError(this.state.error)) {
        return (
          <div style={styles.container}>
            <div style={styles.card}>
              <h1 style={styles.title}>🔒 Authentication Required</h1>
              <p style={styles.message}>Redirecting to login...</p>
            </div>
          </div>
        )
      }

      // Non-auth error - show detailed fallback UI
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>⚠️ Something went wrong</h1>
            <p style={styles.message}>We encountered an unexpected error. Please try refreshing the page.</p>

            {/* Show error details in development */}
            {import.meta.env.DEV && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details (Development Only)</summary>
                <div style={styles.errorDetails}>
                  <p>
                    <strong>Error:</strong> {this.state.error.message}
                  </p>
                  {this.state.error.stack && <pre style={styles.stack}>{this.state.error.stack}</pre>}
                  {this.state.errorInfo && <pre style={styles.stack}>{this.state.errorInfo.componentStack}</pre>}
                </div>
              </details>
            )}

            <div style={styles.actions}>
              <button onClick={this.handleReset} style={styles.button}>
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                style={{ ...styles.button, ...styles.buttonSecondary }}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      )
    }

    // No error - render children normally
    return this.props.children
  }
}

/**
 * Inline styles for error boundary UI
 * Using inline styles to avoid CSS dependency issues during error states
 */
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '40px',
    maxWidth: '600px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#333',
  },
  message: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  details: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#f8f8f8',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
  },
  summary: {
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '12px',
    color: '#666',
  },
  errorDetails: {
    fontSize: '14px',
    color: '#444',
  },
  stack: {
    backgroundColor: '#fff',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '12px',
    fontFamily: 'monospace',
    marginTop: '8px',
    border: '1px solid #e0e0e0',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  button: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#0ea5e9',
    color: 'white',
    transition: 'background-color 0.2s',
  } as React.CSSProperties,
  buttonSecondary: {
    backgroundColor: '#64748b',
  } as React.CSSProperties,
}
